import { useState, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X, ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/utils/cn'
import { useCurrentLanguage } from '@/utils/contentVisibility'
import { getVisibleNavRoutes, getVisibleServicesRoutes } from '@/config/routeVisibility'
import { useUiLabels } from '@/hooks/useUiLabels'
import { useLocale } from '@/hooks/useLocale'

export default function MobileMenu() {
  const [isOpen, setIsOpen] = useState(false)
  const [isServicesOpen, setIsServicesOpen] = useState(false)
  const { t } = useTranslation()
  const { getLabel } = useUiLabels()
  const location = useLocation()
  const { lang, localizePath } = useLocale()
  const currentLang = useCurrentLanguage()

  const mainNav = useMemo(() => getVisibleNavRoutes(currentLang), [currentLang])
  const servicesNav = useMemo(() => getVisibleServicesRoutes(currentLang), [currentLang])

  const menuItems = useMemo(
    () => mainNav.map((item) => ({
      label: getLabel(item.labelKey),
      to: localizePath(item.path),
    })),
    [mainNav, getLabel, localizePath]
  )

  const servicesItems = useMemo(
    () => servicesNav.map((item) => ({
      label: getLabel(item.labelKey),
      to: localizePath(item.path),
      external: false,
    })),
    [servicesNav, getLabel, localizePath]
  )

  // path is already localized (e.g. /ar, /ar/library)
  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/')

  return (
    <>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="md:hidden p-2 text-gray-600 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400 transition-colors"
        aria-label="Toggle menu"
        aria-expanded={isOpen}
      >
        {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />
          <nav
            className={cn(
              'fixed top-0 right-0 h-full w-64 bg-white dark:bg-gray-800 shadow-lg z-50 transform transition-transform duration-300 md:hidden',
              isOpen ? 'translate-x-0' : 'translate-x-full'
            )}
            aria-label="Mobile navigation"
          >
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <div className="text-xl font-bold text-primary-600 dark:text-primary-400">{getLabel('brand.name', 'سكينة')}</div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 text-gray-600 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400"
                  aria-label="Close menu"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <ul className="p-4 space-y-2">
              {menuItems.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    onClick={() => setIsOpen(false)}
                    className={cn(
                      'block px-4 py-3 rounded-lg transition-colors',
                      isActive(item.to)
                        ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 font-medium'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    )}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}

              {/* Services Dropdown – safe links only (US-2.H1) */}
              {servicesItems.length > 0 && (
                <li>
                  <button
                    onClick={() => setIsServicesOpen(!isServicesOpen)}
                    className={cn(
                      'w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors',
                      isActive(localizePath('/services'))
                        ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 font-medium'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    )}
                  >
                    <span>{getLabel('common.services', 'الخدمات')}</span>
                    <ChevronDown className={cn(
                      'w-4 h-4 transition-transform',
                      isServicesOpen && 'rotate-180'
                    )} />
                  </button>
                  {isServicesOpen && (
                    <ul className="mr-4 mt-2 space-y-1">
                      {servicesItems.map((item) => (
                        <li key={item.to}>
                          {item.external ? (
                            <a
                              href={item.to}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() => {
                                setIsServicesOpen(false)
                                setIsOpen(false)
                              }}
                              className={cn(
                                'block px-4 py-2 rounded-lg transition-colors text-sm',
                                'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                              )}
                            >
                              {item.label}
                            </a>
                          ) : (
                            <Link
                              to={item.to}
                              onClick={() => {
                                setIsServicesOpen(false)
                                setIsOpen(false)
                              }}
                              className={cn(
                                'block px-4 py-2 rounded-lg transition-colors text-sm',
                                isActive(item.to)
                                  ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 font-medium'
                                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                              )}
                            >
                              {item.label}
                            </Link>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              )}
            </ul>
          </nav>
        </>
      )}
    </>
  )
}

